import moment from 'moment';
import React, {useEffect, useState} from 'react';
import {
  Modal,
  View,
  Text,
  TouchableOpacity,
  ScrollView,
  TextInput,
  StyleSheet,
  ActivityIndicator,
  Platform,
} from 'react-native';
import {Picker} from '@react-native-picker/picker';
import Icon from '@react-native-vector-icons/fontawesome';
import {useSelector} from 'react-redux';
import {ShowToast} from '../Common/common';
import {useGetACMCompanysQuery} from '../redux/services/acmcompanyApi';
import {useGetFOEOccRoomsQuery} from '../redux/services/foecheckinApi';
import {RootState} from '../redux/store';
import {
  colors,
  spacing,
  shadows,
  borderRadius,
  typography,
} from '../theme/theme';

import T from '../Components/T';
import { tp } from '../utils/translateText';
interface SettlementDialogProps {
  visible: boolean;
  onClose: () => void;
  tableData: any;
  modeData: any[];
  billAmount: number;
  billId: number;
  onSettle: (settlementData: any) => Promise<void>;
}

const SettlementDialog: React.FC<SettlementDialogProps> = ({
  visible,
  onClose,
  tableData,
  modeData,
  billAmount,
  billId,
  onSettle,
}) => {
  const gdata = useSelector((state: RootState) => state.gdata);
  const glogic = useSelector((state: RootState) => state.glogic.glogic);
  const cid = gdata.company;
  const [amounts, setAmounts] = useState<{[key: number]: number}>({});
  const [refNos, setRefNos] = useState<{[key: number]: string}>({});
  const [companies, setCompanies] = useState<{[key: number]: number}>({});
  const [rooms, setRooms] = useState<{[key: number]: number}>({});
  const [isSaving, setIsSaving] = useState(false);

  const {data: companyData} = useGetACMCompanysQuery(cid);
  const {data: occroomData} = useGetFOEOccRoomsQuery(
    {
      cid: cid,
      coid: 0,
    },
    {skip: !visible},
  );

  const companys = companyData?.map(value => ({
    label: value.cp_name,
    id: value.cp_id,
    value: value,
  }));

  const roomsList =
    occroomData?.map(value => ({
      label: value.rm_name,
      id: value.cd_room,
      value: value,
    })) || [];

  useEffect(() => {
    if (visible) {
      setAmounts({});
      setRefNos({});
      setCompanies({});
      setRooms({});
    }
  }, [visible]);

  const handleAmountChange = (modeId: number, amount: number) => {
    setAmounts({...amounts, [modeId]: amount});
  };

  const handleAmountClick = (modeId: number) => {
    const currentAmount = amounts[modeId] || 0;
    const totalPaidWithoutCurrent = Object.entries(amounts)
      .filter(([id]) => Number(id) !== modeId)
      .reduce((sum, [, amt]) => sum + (Number(amt) || 0), 0);
    const balance = billAmount - totalPaidWithoutCurrent;

    if (balance > 0) {
      setAmounts({...amounts, [modeId]: balance});
    } else if (balance === 0) {
      const newAmounts: {[key: number]: number} = {};
      newAmounts[modeId] = billAmount;
      setAmounts(newAmounts);
      setRefNos({});
      setCompanies({});
      setRooms({});
    }
  };

  const handleRefNoChange = (modeId: number, refNo: string) => {
    setRefNos({...refNos, [modeId]: refNo});
  };

  const handleCompanyChange = (modeId: number, companyId: number) => {
    setCompanies({...companies, [modeId]: companyId});
  };

  const handleRoomChange = (modeId: number, roomId: number) => {
    setRooms({...rooms, [modeId]: roomId});
  };

  const calculateTotal = () => {
    return Object.values(amounts).reduce(
      (sum, amt) => sum + (Number(amt) || 0),
      0,
    );
  };

  const handleSubmit = async () => {
    const totalPaid = calculateTotal();
    if (totalPaid < billAmount) {
      ShowToast(
        `Total payment (${totalPaid}) is less than bill amount (${billAmount})`,
        'error',
      );
      return;
    }

    const voidMissing = modeData?.find(
      mode =>
        Number(mode.md_flag) === 7 &&
        (amounts[mode.md_id] || 0) > 0 &&
        (refNos[mode.md_id] || '').trim().length < 3,
    );
    if (voidMissing) {
      ShowToast(`Narration is mandatory for ${voidMissing.md_name}`, 'error');
      return;
    }

    setIsSaving(true);

    try {
      const bsDetails =
        modeData
          ?.filter(mode => {
            const amount = amounts[mode.md_id] || 0;
            return amount > 0;
          })
          .map((mode, index) => {
            const amount = amounts[mode.md_id] || 0;
            const modeType = Number(mode.md_type || 0);
            const isCredit = modeType === 1 || modeType === 2;
            const isRoom = modeType === 3;

            return {
              sd_id: 0,
              sd_srid: index + 1,
              sd_mode: mode.md_id,
              sd_amt: String(amount),
              sd_refno: refNos[mode.md_id] || '',
              sd_refdt: moment().format('YYYY/MM/DD HH:mm:ss'),
              sd_refbank: '',
              sd_company: isCredit
                ? companies[mode.md_id] || 0
                : isRoom
                ? rooms[mode.md_id] || 0
                : 0,
              sd_room: isRoom ? rooms[mode.md_id] || 0 : 0,
            };
          }) || [];

      if (bsDetails.length === 0) {
        ShowToast('Please enter at least one payment amount', 'error');
        setIsSaving(false);
        return;
      }

      const settlementData = {
        bs_id: 0,
        bs_bill: billId,
        bs_date: moment().format('YYYY-MM-DD HH:mm:ss'),
        bs_amt: String(billAmount),
        bs_disc: '0',
        bs_tips: '0',
        bs_receivedamt: String(totalPaid),
        bs_total: String(totalPaid),
        bs_flg: 0,
        bs_uid: gdata.user,
        bs_cdt: moment().format('YYYY-MM-DD HH:mm:ss'),
        bs_muid: gdata.user,
        bs_mdt: moment().format('YYYY-MM-DD HH:mm:ss'),
        bs_details: bsDetails,
      };

      await onSettle(settlementData);

      ShowToast(`Bill successfully settled. Amount: ₹${totalPaid}`);
      onClose();
    } catch (error: any) {
      console.error('Settlement Error:', error);
      ShowToast(
        error?.data?.message ||
          'An error occurred while processing the settlement. Please try again.',
        'error',
      );
    } finally {
      setIsSaving(false);
    }
  };

  const totalPaid = calculateTotal();
  const balance = billAmount - totalPaid;

  return (
    <Modal
      visible={visible}
      transparent={true}
      animationType="slide"
      onRequestClose={onClose}>
      <View style={styles.modalOverlay}>
        <View style={styles.modalContent}>
          {/* Header */}
          <View style={styles.header}>
            <Text style={styles.headerTitle}>
              <T>💳 Settlement -</T> {tableData?.tb_name || 'Table'}
            </Text>
            <TouchableOpacity onPress={onClose} style={styles.closeButton}>
              <Icon name="close" size={24} color={colors.white} />
            </TouchableOpacity>
          </View>

          <ScrollView
            style={styles.content}
            showsVerticalScrollIndicator={false}>
            {/* Bill Summary */}
            <View style={styles.summaryCard}>
              <View style={styles.summaryRow}>
                <View style={styles.summaryItem}>
                  <Text style={styles.summaryLabel}><T>Bill Amount</T></Text>
                  <Text style={[styles.summaryValue, {color: colors.primary}]}>
                    ₹{billAmount.toFixed(2)}
                  </Text>
                </View>
                <View style={styles.summaryItem}>
                  <Text style={styles.summaryLabel}><T>Total Paid</T></Text>
                  <Text
                    style={[
                      styles.summaryValue,
                      {
                        color:
                          totalPaid >= billAmount
                            ? colors.success
                            : colors.warning,
                      },
                    ]}>
                    ₹{totalPaid.toFixed(2)}
                  </Text>
                </View>
                <View style={styles.summaryItem}>
                  <Text style={styles.summaryLabel}><T>Balance</T></Text>
                  <Text
                    style={[
                      styles.summaryValue,
                      {
                        color: balance <= 0 ? colors.success : colors.error,
                      },
                    ]}>
                    ₹{balance.toFixed(2)}
                  </Text>
                </View>
              </View>
            </View>

            {/* Payment Methods */}
            <Text style={styles.sectionTitle}><T>Payment Methods</T></Text>
            {modeData?.map(mode => {
              const needsRefNo = Number(mode.md_flag) > 1;
              const isCredit = [1, 2].includes(Number(mode.md_type || 0));
              const isVoid = Number(mode.md_flag) === 7;
              const isRoom = Number(mode.md_type || 0) === 3;
              const amount = amounts[mode.md_id] || 0;
              const hasValue = amount > 0;

              return (
                <View
                  key={mode.md_id}
                  style={[
                    styles.paymentCard,
                    hasValue && styles.paymentCardActive,
                  ]}>
                  <Text
                    style={[
                      styles.paymentMethodTitle,
                      hasValue && {color: colors.success},
                    ]}>
                    {mode.md_name}
                  </Text>

                  <View style={styles.amountInputContainer}>
                    <Text style={styles.currencySymbol}>₹</Text>
                    <TextInput
                      style={styles.amountInput}
                      keyboardType="numeric"
                      placeholder="0"
                      value={amount > 0 ? amount.toString() : ''}
                      onFocus={() => handleAmountClick(mode.md_id)}
                      onChangeText={txt =>
                        handleAmountChange(mode.md_id, Number(txt) || 0)
                      }
                    />
                  </View>

                  {needsRefNo && (
                    <TextInput
                      style={[
                        styles.refInput,
                        !hasValue && styles.refInputDisabled,
                      ]}
                      placeholder={isVoid ? tp("Narration (mandatory)") : tp("Reference No")}
                      maxLength={45}
                      value={refNos[mode.md_id] || ''}
                      onChangeText={txt => handleRefNoChange(mode.md_id, txt)}
                      editable={hasValue}
                    />
                  )}

                  {isCredit && (
                    <View style={styles.selectContainer}>
                      <Text style={styles.selectLabel}>
                        <T>Select Company for Credit</T>
                      </Text>
                      <View style={styles.pickerWrapper}>
                        <Picker
                          selectedValue={companies[mode.md_id] || 0}
                          onValueChange={(v: any) =>
                            handleCompanyChange(mode.md_id, Number(v))
                          }
                          enabled={hasValue}
                          style={styles.picker}>
                          <Picker.Item label="--Select--" value={0} />
                          {companys?.map(company => (
                            <Picker.Item
                              key={company.id}
                              label={company.label}
                              value={company.id}
                            />
                          ))}
                        </Picker>
                      </View>
                    </View>
                  )}

                  {isRoom && (
                    <View style={styles.selectContainer}>
                      <Text style={styles.selectLabel}><T>Select Room</T></Text>
                      <View style={styles.pickerWrapper}>
                        <Picker
                          selectedValue={rooms[mode.md_id] || 0}
                          onValueChange={(v: any) =>
                            handleRoomChange(mode.md_id, Number(v))
                          }
                          enabled={hasValue}
                          style={styles.picker}>
                          <Picker.Item label="--Select--" value={0} />
                          {roomsList?.map(room => (
                            <Picker.Item
                              key={room.id}
                              label={room.label}
                              value={room.id}
                            />
                          ))}
                        </Picker>
                      </View>
                    </View>
                  )}
                </View>
              );
            })}
          </ScrollView>

          {/* Footer */}
          <View style={styles.footer}>
            <TouchableOpacity
              style={[styles.button, styles.cancelButton]}
              onPress={onClose}
              disabled={isSaving}>
              <Text style={styles.cancelButtonText}><T>Cancel</T></Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={[
                styles.button,
                styles.submitButton,
                (balance > 0 || isSaving) && styles.submitButtonDisabled,
              ]}
              onPress={handleSubmit}
              disabled={balance > 0 || isSaving}>
              {isSaving ? (
                <ActivityIndicator color={colors.white} />
              ) : (
                <Text style={styles.submitButtonText}><T>Settle Bill</T></Text>
              )}
            </TouchableOpacity>
          </View>
        </View>
      </View>
    </Modal>
  );
};

const styles = StyleSheet.create({
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 1000,
    elevation: Platform.OS === 'android' ? 50 : undefined,
  },
  modalContent: {
    width: '90%',
    maxHeight: '90%',
    backgroundColor: colors.white,
    borderRadius: borderRadius.xl,
    overflow: 'hidden',
    zIndex: 1001,
    elevation: Platform.OS === 'android' ? 51 : undefined,
    ...shadows.lg,
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: spacing.lg,
    backgroundColor: colors.primary,
  },
  headerTitle: {
    fontSize: typography.fontSize.xl,
    fontWeight: typography.fontWeight.bold,
    color: colors.white,
    flex: 1,
  },
  closeButton: {
    padding: spacing.xs,
  },
  content: {
    padding: spacing.base,
    maxHeight: '70%',
  },
  summaryCard: {
    backgroundColor: colors.infoLight,
    padding: spacing.base,
    borderRadius: borderRadius.lg,
    marginBottom: spacing.lg,
  },
  summaryRow: {
    flexDirection: 'row',
    justifyContent: 'space-around',
  },
  summaryItem: {
    alignItems: 'center',
  },
  summaryLabel: {
    fontSize: typography.fontSize.sm,
    color: colors.textSecondary,
    marginBottom: spacing.xs,
  },
  summaryValue: {
    fontSize: typography.fontSize.xl,
    fontWeight: typography.fontWeight.bold,
  },
  sectionTitle: {
    fontSize: typography.fontSize.lg,
    fontWeight: typography.fontWeight.bold,
    color: colors.textPrimary,
    marginBottom: spacing.base,
  },
  paymentCard: {
    backgroundColor: colors.backgroundSecondary,
    padding: spacing.base,
    borderRadius: borderRadius.lg,
    marginBottom: spacing.base,
    borderWidth: 1,
    borderColor: colors.borderLight,
  },
  paymentCardActive: {
    backgroundColor: colors.successLight,
    borderWidth: 2,
    borderColor: colors.success,
  },
  paymentMethodTitle: {
    fontSize: typography.fontSize.base,
    fontWeight: typography.fontWeight.bold,
    color: colors.textPrimary,
    marginBottom: spacing.sm,
  },
  amountInputContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: colors.white,
    borderRadius: borderRadius.base,
    paddingHorizontal: spacing.sm,
    marginBottom: spacing.sm,
    borderWidth: 1,
    borderColor: colors.borderLight,
  },
  currencySymbol: {
    fontSize: typography.fontSize.base,
    fontWeight: typography.fontWeight.bold,
    color: colors.textPrimary,
    marginRight: spacing.xs,
  },
  amountInput: {
    flex: 1,
    fontSize: typography.fontSize.base,
    color: colors.textPrimary,
    paddingVertical: spacing.sm,
  },
  refInput: {
    backgroundColor: colors.white,
    borderRadius: borderRadius.base,
    padding: spacing.sm,
    marginBottom: spacing.sm,
    borderWidth: 1,
    borderColor: colors.borderLight,
    fontSize: typography.fontSize.base,
  },
  refInputDisabled: {
    backgroundColor: colors.backgroundTertiary,
    color: colors.textTertiary,
  },
  selectContainer: {
    marginBottom: spacing.sm,
  },
  selectLabel: {
    fontSize: typography.fontSize.sm,
    fontWeight: typography.fontWeight.medium,
    color: colors.textSecondary,
    marginBottom: spacing.xs,
  },
  pickerWrapper: {
    backgroundColor: colors.white,
    borderRadius: borderRadius.base,
    borderWidth: 1,
    borderColor: colors.borderLight,
    overflow: 'hidden',
  },
  picker: {
    height: 50,
  },
  footer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    padding: spacing.base,
    backgroundColor: colors.backgroundTertiary,
    borderTopWidth: 1,
    borderTopColor: colors.borderLight,
  },
  button: {
    flex: 1,
    paddingVertical: spacing.base,
    borderRadius: borderRadius.base,
    alignItems: 'center',
    justifyContent: 'center',
    marginHorizontal: spacing.xs,
  },
  cancelButton: {
    backgroundColor: colors.backgroundSecondary,
  },
  cancelButtonText: {
    fontSize: typography.fontSize.base,
    fontWeight: typography.fontWeight.semibold,
    color: colors.textPrimary,
  },
  submitButton: {
    backgroundColor: colors.success,
  },
  submitButtonDisabled: {
    backgroundColor: colors.textTertiary,
  },
  submitButtonText: {
    fontSize: typography.fontSize.base,
    fontWeight: typography.fontWeight.semibold,
    color: colors.white,
  },
});

export default SettlementDialog;

